<template>
  <div class="pt-2 h-full flex flex-col overflow-hidden">
    <!-- Main Content -->
    <div class="flex-1 overflow-hidden">
      <v-row>
        <!-- Left Sidebar - Department List -->
        <v-col cols="12" md="4" lg="3" class="pr-0 overflow-hidden">
          <v-card flat class="h-full form-card flex flex-col overflow-hidden">
            <v-card-title class="text-base font-bold text-gray-800 flex-shrink-0 overflow-hidden">
              Departments
              <div class="flex justify-between w-full">
                <v-text-field
                  label="Search department"
                  class="text-input flex-grow"
                  v-model="searchTerm"
                  outlined
                  flat
                  hide-details
                  solo
                  dense
                  clearable
                  prepend-inner-icon="mdi-magnify"
                  placeholder="Search department"
                >
                </v-text-field>
              </div>
            </v-card-title>

            <v-card-text class="pt-0 flex-1 overflow-hidden">
              <div class="space-y-3 h-full overflow-x-hidden">
                <div
                  v-for="department in filteredDepartments"
                  :key="department._id"
                  class="cursor-pointer flex justify-between items-center relative p-2 overflow-hidden"
                  :class="[
                    selectedDepartment && selectedDepartment._id === department._id
                      ? 'border border-blue-500 bg-white'
                      : 'border border-gray-200'
                  ]"
                  outlined
                  @click="selectDepartment(department)"
                >
                  <v-card-text class="py-3 overflow-hidden">
                    <h3 class="font-semibold text-gray-800 mb-1 overflow-hidden">
                      {{ department?.departmentName }}
                    </h3>
                  </v-card-text>
                  <div class="flex items-center absolute top-2 right-2 gap-2 my-4">
                    <v-icon
                      v-if="filteredDepartments.length && filteredDepartments.length > 1"
                      color="error"
                      small
                      v-iam-disabled="'organization.departments.delete'"
                      @click.stop="deleteDepartment(department)"
                      class="cursor-pointer hover:opacity-80"
                    >
                      mdi-delete
                    </v-icon>
                    <v-icon
                      color="primary"
                      small
                      v-iam-disabled="'organization.departments.update'"
                      @click.stop="editDepartment(department)"
                      class="cursor-pointer hover:opacity-80"
                    >
                      mdi-pencil
                    </v-icon>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Right Panel - Department Details -->
        <v-col cols="12" md="8" lg="9" class="form-content overflow-hidden">
          <v-card
            class="h-full form-card flex flex-col overflow-hidden"
            v-if="selectedDepartment"
            flat
          >
            <!-- Department Header -->
            <v-card-title class="border-b border-gray-200 pb-4 flex-shrink-0 overflow-hidden">
              <div class="flex-1 overflow-hidden">
                <h2 class="text-base font-bold text-gray-800 mb-2 overflow-hidden">
                  {{ selectedDepartment?.departmentName }}
                </h2>
                <div class="space-y-1 text-xs text-gray-600 overflow-hidden">
                  <p class="overflow-hidden">
                    Description: {{ selectedDepartment.departmentDescription }}
                  </p>
                </div>
              </div>
              <v-btn
                color="primary"
                v-iam-disabled="'organization.departments.create'"
                @click="showDesignationModal = true"
                class="font-medium overflow-hidden"
                style="text-transform: none"
                outlined
              >
                <v-icon left>mdi-plus</v-icon>
                Add Designation
              </v-btn>
            </v-card-title>

            <v-card-text class="flex-1 overflow-hidden">
              <!-- Designation Management -->
              <div class="mt-4 h-full overflow-hidden">
                <div
                  v-if="
                    selectedDepartment.designations && selectedDepartment.designations.length > 0
                  "
                  class="h-full flex flex-col overflow-hidden"
                >
                  <h3 class="text-lg font-semibold mb-4 flex-shrink-0 overflow-hidden">
                    Designations
                  </h3>

                  <!-- Designation List -->
                  <div class="space-y-2 mb-6 flex-1 overflow-x-hidden">
                    <v-card
                      v-for="designation in selectedDepartment.designations"
                      :key="designation._id"
                      :class="[
                        'team-item-hover cursor-pointer transition-colors duration-200 overflow-hidden',
                        selectedDesignation?._id === designation._id
                          ? 'bg-blue-50 border-blue-200'
                          : ''
                      ]"
                      outlined
                      @click="selectDesignation(designation)"
                    >
                      <v-card-text class="py-3 overflow-hidden">
                        <h4 class="font-semibold text-gray-800 overflow-hidden">
                          {{ designation?.designationName }}
                        </h4>
                        <p class="text-sm text-gray-600 overflow-hidden">
                          {{ designation?.designationDescription }}
                        </p>
                      </v-card-text>
                      <div class="flex items-center absolute top-2 right-2 gap-2">
                        <v-icon
                          small
                          color="error"
                          v-iam-disabled="'organization.departments.delete'"
                          @click.stop="deleteDesignation(designation)"
                        >
                          {{ isDeleting ? 'mdi-loading mdi-spin' : 'mdi-delete' }}
                        </v-icon>
                        <v-icon small v-iam-disabled="'organization.departments.update'" @click.stop="editDesignation(designation)">mdi-pencil</v-icon>
                      </div>
                    </v-card>
                  </div>
                </div>

                <!-- Show NoData component when no designations exist -->
                <div v-else>
                  <NoData
                    title="No Designations Found"
                    subTitle="This department doesn't have any designations yet. Create your first designation to get started."
                    btnText="Create Designation"
                    :btnAction="() => (showDesignationModal = true)"
                  />
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- No Department Selected -->
          <v-card
            v-else
            class="h-full flex items-center justify-center overflow-hidden form-card"
            flat
          >
            <v-card-text class="text-center overflow-hidden">
              <v-icon size="64" color="grey lighten-2" class="mb-4">mdi-office-building</v-icon>
              <p class="text-lg text-gray-500 overflow-hidden">
                Add department for adding the designations.
              </p>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Add Department Modal -->
    <v-dialog v-model="showDepartmentModal" max-width="700px" persistent>
      <v-card>
        <ActitiveTabcomponent
          :tabs="dynamicDepartmentTabs"
          defaultTab="addDepartment"
          insideDialog
        >
          <template #tab-buttons>
            <v-btn icon @click="closeDepartmentModal">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </template>

          <template #addDepartment>
            <div>
              <div class="help-text text-start mx-4 my-2">
                <strong>Department:</strong> The functional division or team the employee belongs to.
                <br />
                <strong>Examples:</strong> Human Resources, Information Technology, Finance, Marketing,
                Operations
                <br />
                <em>Note: Used for reporting, team management, and organizational structure.</em>
              </div>
              <div class="ma-4">
                <v-form ref="departmentForm" v-model="departmentFormValid">
                  <v-text-field
                    v-model="newDepartment.name"
                    label="Department Name *"
                    dense
                    hide-details
                    class="mb-2"
                    outlined
                    :rules="[(v) => !!v || 'Department name is required']"
                    @input="isDepartmentEdited = true"
                    prepend-inner-icon="mdi-office-building"
                  ></v-text-field>
                  <v-textarea
                    v-model="newDepartment.description"
                    label="Description"
                    dense
                    outlined
                    rows="3"
                    hide-details
                    class="mb-4"
                    @input="isDepartmentEdited = true"
                    prepend-inner-icon="mdi-text"
                  ></v-textarea>
                </v-form>
              </div>
              <v-divider></v-divider>
              <v-card-actions class="pa-4 pt-3">
                <v-spacer></v-spacer>
                <v-btn
                  text
                  @click="closeDepartmentModal"
                  :disabled="isSaving"
                  style="text-transform: none"
                  >Cancel</v-btn
                >
                <v-btn
                  color="primary"
                  v-iam-disabled="newDepartment.id ? 'organization.departments.update' : 'organization.departments.create'"
                  @click="saveDepartment"
                  :disabled="
                    !departmentFormValid || isSaving || (newDepartment.id && !isDepartmentEdited)
                  "
                  :loading="isSaving"
                  style="text-transform: none"
                >
                  <v-icon left>mdi-content-save</v-icon>
                  Save
                </v-btn>
              </v-card-actions>
            </div>
          </template>
        </ActitiveTabcomponent>
      </v-card>
    </v-dialog>

    <!-- Add Designation Modal -->
    <v-dialog v-model="showDesignationModal" persistent max-width="700px">
      <v-card>
        <ActitiveTabcomponent
          :tabs="dynamicDesignationTabs"
          defaultTab="addDesignation"
          insideDialog
        >
          <template #tab-buttons>
            <v-btn icon @click="closeDesignationModal">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </template>

          <template #addDesignation>
            <div>
              <!-- Tooltip/Help Text -->
              <div class="help-text text-start mx-4 my-2">
                <strong>Designation:</strong> The official job title or position in the company.
                <br />
                <strong>Examples:</strong> Software Engineer, Senior Manager, HR Executive, Team Lead
                <br />
                <em>Note: This appears on business cards, org charts, and official documents.</em>
              </div>
              <div class="ma-4">
                <v-form ref="designationForm" v-model="designationFormValid">
                  <!-- <div class="p-4 pt-2">
                    <p class="text-sm pb-2 font-bold text-gray-700">Basic info</p> -->
                  <v-text-field
                    v-model="newDesignation.name"
                    label="Designation Name *"
                    dense
                    outlined
                    hide-details
                    class="mb-2"
                    :rules="[
                      (v) => !!v || 'Designation name is required',
                      (v) => (v && v.length >= 2) || 'Designation name must be at least 2 characters'
                    ]"
                    @input="isDesignationEdited = true"
                    prepend-inner-icon="mdi-account-tie"
                  ></v-text-field>
                  <v-textarea
                    v-model="newDesignation.description"
                    label="Description"
                    dense
                    outlined
                    hide-details
                    rows="3"
                    class="mb-4"
                    @input="isDesignationEdited = true"
                    prepend-inner-icon="mdi-text"
                  ></v-textarea>
                  <v-text-field
                    v-model="newDesignation.probationPeriod"
                    label="Probation Period (days)"
                    dense
                    outlined
                    hide-details
                    class="mb-2"
                    type="number"
                    @input="isDesignationEdited = true"
                    prepend-inner-icon="mdi-clock-outline"
                  ></v-text-field>
                  <v-text-field
                    v-model="newDesignation.noticePeriod"
                    label="Notice Period (days)"
                    dense
                    outlined
                    hide-details
                    class="mb-2"
                    type="number"
                    @input="isDesignationEdited = true"
                    prepend-inner-icon="mdi-calendar-alert"
                  ></v-text-field>
                  <!-- </div> -->
                </v-form>
              </div>
              <v-divider></v-divider>
              <v-card-actions class="pa-4 pt-3">
                <v-spacer></v-spacer>
                <v-btn
                  text
                  @click="closeDesignationModal"
                  :disabled="isSaving"
                  style="text-transform: none"
                  >Cancel</v-btn
                >
                <v-btn
                  color="primary"
                  v-iam-disabled="newDesignation.id ? 'organization.designations.update' : 'organization.designations.create'"
                  @click="saveDesignation"
                  :disabled="
                    !designationFormValid || isSaving || (newDesignation.id && !isDesignationEdited)
                  "
                  :loading="isSaving"
                  style="text-transform: none"
                >
                  <v-icon left>mdi-content-save</v-icon>
                  Save
                </v-btn>
              </v-card-actions>
            </div>
          </template>
        </ActitiveTabcomponent>
      </v-card>
    </v-dialog>
    <!-- Delete Confirmation Dialog -->
    <AlertPopUp
      :show-confirmation-dialog="showDeleteDialog"
      confirmation-title="Confirm Delete"
      :confirmation-message="
        deleteErrorMessage ||
        (deleteType === 'department'
          ? 'Are you sure you want to delete this department? This action cannot be undone.'
          : 'Are you sure you want to delete this designation? This action cannot be undone.')
      "
      confirmation-action-text="Delete"
      confirmation-action-color="error"
      :perform-action="confirmDelete"
      :cancel="cancelDelete"
      :error-message="deleteErrorMessage"
    />
  </div>
</template>

<script>
import NoData from '@/components/NoData.vue'
import AlertPopUp from '@/components/AlertPopUp.vue'
import { axiosUrl } from '@/utils/workforceApi'
import { hasIamPermission } from '@/utils/iamEvaluator'
import ActitiveTabcomponent from '@/components/ActitiveTabcomponent.vue'

export default {
  name: 'DepartmentDesignation',
  components: {
    NoData,
    AlertPopUp,
    ActitiveTabcomponent
  },
  data() {
    return {
      // Modal states
      showDepartmentModal: false,
      showDesignationModal: false,
      departmentFormValid: false,
      designationFormValid: false,

      searchTerm: '',

      // Selected items
      selectedDepartment: null,
      selectedDesignation: null,

      // Form data
      newDepartment: {
        name: '',
        description: ''
      },
      newDesignation: {
        name: '',
        description: '',
        probationPeriod: '',
        noticePeriod: ''
      },

      // Data from API
      departments: [],

      // Loading states
      isDeleting: false,
      isSaving: false,

      // Edit tracking flags
      isDepartmentEdited: false,
      isDesignationEdited: false,

      // Confirmation dialog states
      showDeleteDialog: false,
      itemToDelete: null,
      deleteType: null, // 'department' or 'designation'

      // Error message for deletion
      deleteErrorMessage: null
    }
  },
  computed: {
    iamStatements() {
      return this.$store.state.wfmStatements || []
    },
    filteredDepartments() {
      if (!this.searchTerm) return this.departments
      return this.departments.filter((department) =>
        department.departmentName.toLowerCase().includes(this.searchTerm.toLowerCase())
      )
    },
    dynamicDepartmentTabs() {
      return [
        {
          key: 'addDepartment',
          label: this.newDepartment.id ? 'Edit Department' : 'Create Department',
          icon: this.newDepartment.id ? 'mdi-pencil' : 'mdi-plus',
          component: 'DepartmentDesignation'
        }
      ]
    },
    dynamicDesignationTabs() {
      return [
        {
          key: 'addDesignation',
          label: this.newDesignation.id ? 'Edit Designation' : 'Create Designation',
          icon: this.newDesignation.id ? 'mdi-pencil' : 'mdi-plus',
          component: 'DepartmentDesignation'
        }
      ]
    }
  },
  async mounted() {
    await this.fetchDepartmentsAndDesignations()
    if (this.departments.length > 0) {
      this.selectDepartment(this.departments[0])
    }
  },
  methods: {
    hasIamPermission,
    async fetchDepartmentsAndDesignations() {
      try {
        // Fetch departments
        const departmentsResponse = await this.$axios.get(axiosUrl.departments.getDepartments)
        this.departments = departmentsResponse.data.departments || []

        // Fetch designations
        const designationsResponse = await this.$axios.get(axiosUrl.designations.getDesignations)
        const designations = designationsResponse.data.designations || []

        // Associate designations with departments
        this.departments = this.departments.map((department) => ({
          ...department,
          departmentDescription: department.departmentDescription || '',
          designations: designations.filter(
            (designation) => designation.department?._id === department._id
          )
        }))
      } catch (error) {
        console.error('Error fetching departments and designations:', error)
        this.$toast.error('Failed to fetch departments and designations')
      }
    },

    selectDepartment(department) {
      this.selectedDepartment = { ...department }

      if (this.selectedDepartment.designations?.length > 0) {
        this.selectDesignation(this.selectedDepartment.designations[0])
      } else {
        this.selectedDesignation = null
      }
    },

    selectDesignation(designation) {
      this.selectedDesignation = { ...designation }
    },

    async deleteDesignation(designation) {
      if (!designation) return
      // Set the designation to delete and show confirmation dialog
      this.itemToDelete = designation
      this.deleteType = 'designation'
      this.deleteErrorMessage = null // Clear any previous error message
      this.showDeleteDialog = true
    },
    async deleteDepartment(department) {
      if (!department) return
      // Set the department to delete and show confirmation dialog
      this.itemToDelete = department
      this.deleteType = 'department'
      this.deleteErrorMessage = null // Clear any previous error message
      this.showDeleteDialog = true
    },

    confirmDelete() {
      if (!this.itemToDelete) return
      this.showDeleteDialog = false
      if (this.deleteType === 'department') {
        this.performDeleteDepartment(this.itemToDelete)
      } else if (this.deleteType === 'designation') {
        this.performDeleteDesignation(this.itemToDelete)
      }
    },

    cancelDelete() {
      this.showDeleteDialog = false
      this.itemToDelete = null
      this.deleteType = null
      this.deleteErrorMessage = null
    },

    async performDeleteDesignation(designation) {
      try {
        this.isDeleting = true
        this.deleteErrorMessage = null

        const response = await this.$axios.delete(
          `${axiosUrl.designations.deleteDesignations}/${designation._id}`
        )
        this.$toast.success(response?.data?.message || 'Designation deleted successfully')

        // Remove the designation from the UI immediately without full refresh
        if (this.selectedDepartment && this.selectedDepartment.designations) {
          const designationIndex = this.selectedDepartment.designations.findIndex(
            (d) => d._id === designation._id
          )
          if (designationIndex !== -1) {
            // Use Vue's $delete to maintain reactivity
            this.$delete(this.selectedDepartment.designations, designationIndex)
          }

          // If we just deleted the selected designation, clear the selection
          if (this.selectedDesignation && this.selectedDesignation._id === designation._id) {
            this.selectedDesignation = null
          }

          // If there are no more designations, clear the selected designation
          if (this.selectedDepartment.designations.length === 0) {
            this.selectedDesignation = null
          }
          // If there are still designations but we deleted the selected one, select the first one
          else if (this.selectedDesignation && this.selectedDesignation._id === designation._id) {
            this.selectDesignation(this.selectedDepartment.designations[0])
          }
        }

        // Also update the departments array to reflect the change
        const deptIndex = this.departments.findIndex((d) => d._id === this.selectedDepartment._id)
        if (deptIndex !== -1) {
          const designationIndex = this.departments[deptIndex].designations.findIndex(
            (d) => d._id === designation._id
          )
          if (designationIndex !== -1) {
            this.$delete(this.departments[deptIndex].designations, designationIndex)
          }
        }
      } catch (error) {
        console.error('Error deleting designation:', error)
        const errorMessage = error.response?.data?.message || 'Failed to delete designation'
        this.deleteErrorMessage = errorMessage
        // Keep the dialog open to show the error message
        this.showDeleteDialog = true
      } finally {
        this.isDeleting = false
        if (!this.deleteErrorMessage) {
          this.itemToDelete = null
          this.deleteType = null
        }
      }
    },

    async performDeleteDepartment(department) {
      try {
        this.deleteErrorMessage = null
        const response = await this.$axios.delete(
          `${axiosUrl.departments.deleteDepartments}/${department._id}`
        )
        const successMessage = response?.data?.message || 'Department deleted successfully'
        this.$toast.success(successMessage)
        await this.fetchDepartmentsAndDesignations()
      } catch (error) {
        console.error('Error deleting department:', error)
        const errorMessage = error.response?.data?.message || 'Failed to delete department'
        this.deleteErrorMessage = errorMessage
        // Keep the dialog open to show the error message
        this.showDeleteDialog = true
      } finally {
        if (!this.deleteErrorMessage) {
          this.itemToDelete = null
          this.deleteType = null
        }
      }
    },

    // Modal methods
    closeDepartmentModal() {
      this.showDepartmentModal = false
      this.resetDepartmentForm()
    },

    closeDesignationModal() {
      this.showDesignationModal = false
      this.resetDesignationForm()
    },

    resetDepartmentForm() {
      this.newDepartment = {
        name: '',
        description: ''
      }
      this.isDepartmentEdited = false
      if (this.$refs.departmentForm) {
        this.$refs.departmentForm.resetValidation()
      }
    },

    resetDesignationForm() {
      this.newDesignation = {
        name: '',
        description: '',
        probationPeriod: '',
        noticePeriod: ''
      }
      this.isDesignationEdited = false
      if (this.$refs.designationForm) {
        this.$refs.designationForm.resetValidation()
      }
    },

    async saveDepartment() {
      if (!this.departmentFormValid || this.isSaving) return

      try {
        this.isSaving = true

        const payload = {
          departmentName: this.newDepartment.name,
          departmentDescription: this.newDepartment.description
        }

        let response
        let departmentId
        let successMessage
        let isUpdate = !!this.newDepartment.id

        if (isUpdate) {
          // Update existing department
          response = await this.$axios.put(
            `${axiosUrl.departments.putDepartments}/${this.newDepartment.id}`,
            payload
          )
          departmentId = this.newDepartment.id
          successMessage = response?.data?.message || 'Department updated successfully'

          // Update local state
          const departmentIndex = this.departments.findIndex((d) => d._id === departmentId)
          if (departmentIndex !== -1) {
            this.departments[departmentIndex] = {
              ...this.departments[departmentIndex],
              departmentName: this.newDepartment.name,
              departmentDescription: this.newDepartment.description
            }
          }
        } else {
          // Create new department
          response = await this.$axios.post(axiosUrl.departments.postDepartments, [payload])
          departmentId = response.data.department[0]._id

          const newDepartment = {
            _id: departmentId,
            departmentName: this.newDepartment.name,
            departmentDescription: this.newDepartment.description,
            designations: []
          }

          this.departments.push(newDepartment)
          successMessage = response?.data?.message || 'Department created successfully'
        }

        this.$toast.success(successMessage)
        this.closeDepartmentModal()
        const targetDepartment = this.departments.find(
          (department) => department._id === departmentId
        )
        if (targetDepartment) {
          this.selectDepartment(targetDepartment)
        }
      } catch (error) {
        console.error('Error saving department:', error)
        this.$toast.error(error.response?.data?.message || 'Failed to save department')
      } finally {
        this.isSaving = false
      }
    },

    async saveDesignation() {
      if (!this.designationFormValid || this.isSaving) return

      try {
        this.isSaving = true

        const payload = {
          department: this.selectedDepartment?._id,
          designationName: this.newDesignation.name,
          designationDescription: this.newDesignation.description,
          probationPeriod: this.newDesignation.probationPeriod,
          noticePeriod: this.newDesignation.noticePeriod
        }

        let response
        let isUpdate = !!this.newDesignation.id

        if (isUpdate) {
          response = await this.$axios.put(
            `${axiosUrl.designations.putDesignations}/${this.newDesignation.id}`,
            payload
          )
        } else {
          response = await this.$axios.post(axiosUrl.designations.postDesignations, [payload])
        }

        const successMessage =
          response.data.message || `Designation ${isUpdate ? 'updated' : 'created'} successfully`
        this.$toast.success(successMessage)

        // Store the current department ID to reselect it after refresh
        const currentDepartmentId = this.selectedDepartment?._id

        // Store the new designation ID if creating (to select it after refresh)
        let newDesignationId = null
        if (!isUpdate && response.data.designations && response.data.designations.length > 0) {
          newDesignationId = response.data.designations[0]._id
        }

        // Refresh data to include new designation
        await this.fetchDepartmentsAndDesignations()

        // Reselect the department to maintain UI state
        if (currentDepartmentId) {
          const departmentToSelect = this.departments.find((d) => d._id === currentDepartmentId)
          if (departmentToSelect) {
            this.selectDepartment(departmentToSelect)

            // If we just created a new designation, select it
            if (newDesignationId) {
              const designationToSelect = departmentToSelect.designations?.find(
                (d) => d._id === newDesignationId
              )
              if (designationToSelect) {
                this.selectDesignation(designationToSelect)
              }
            }
          }
        }

        this.closeDesignationModal()
      } catch (error) {
        console.error('Error saving designation:', error)
        this.$toast.error(error.response?.data?.message || 'Failed to save designation')
      } finally {
        this.isSaving = false
      }
    },

    editDepartment(department) {
      this.newDepartment = {
        id: department._id,
        name: department.departmentName,
        description: department.departmentDescription
      }
      this.isDepartmentEdited = false
      this.showDepartmentModal = true
    },

    editDesignation(designation) {
      this.newDesignation = {
        id: designation._id,
        name: designation.designationName,
        description: designation.designationDescription,
        probationPeriod: designation.probationPeriod,
        noticePeriod: designation.noticePeriod
      }
      this.isDesignationEdited = false
      this.showDesignationModal = true
    },

    addDepartment() {
      this.isDepartmentEdited = false
      this.showDepartmentModal = true
    }
  }
}
</script>

<style scoped>
/* Simple overflow management like Roles.vue */
.overflow-hidden {
  overflow: hidden !important;
}

.overflow-y-auto {
  overflow-y: auto !important;
  overflow-x: hidden !important;
}

.overflow-x-hidden {
  overflow-x: hidden !important;
}

/* Team item hover effect */
.team-item-hover:hover {
  background-color: #f7fafc;
  border-color: #e2e8f0;
}

/* Member divider styling */
.member-divider {
  border-bottom: 1px solid #e2e8f0;
}

.member-divider:last-child {
  border-bottom: none;
}

/* Ensure all Vuetify components have proper spacing for labels */
.v-text-field,
.v-textarea,
.v-select,
.v-autocomplete {
  margin-top: 8px !important;
  margin-bottom: 8px !important;
}

/* Prevent label cutoff by ensuring adequate space */
.v-text-field .v-label,
.v-select .v-label,
.v-autocomplete .v-label {
  transform-origin: top left !important;
}

/* Ensure form fields have proper height and spacing */
.v-input--is-focused .v-label,
.v-input--has-state .v-label {
  transform: translateY(-18px) scale(0.75) !important;
}

/* Dialog form content with proper spacing */
.form-content .v-input {
  margin-top: 4px !important;
  margin-bottom: 4px !important;
}
.form-card {
  border: 1px solid #e0e0e0;
  border-radius: 0px !important;
}

/* Remove excessive overflow hidden that causes label cutoff */
.v-text-field,
.v-textarea,
.v-select,
.v-autocomplete,
.v-input {
  overflow: visible !important;
}

.v-text-field >>> .v-input__control,
.v-textarea >>> .v-input__control,
.v-select >>> .v-input__control,
.v-autocomplete >>> .v-input__control {
  overflow: visible !important;
}

/* Dialog content overflow management with proper spacing */
.v-dialog .v-card {
  overflow: visible !important;
}
.v-dialog .v-card .v-card_title {
  overflow: hidden !important;
  padding: 0 !important;
}

.v-dialog .v-card-text {
  overflow-y: auto !important;
  overflow-x: hidden !important;
}

/* Ensure form input fields have proper spacing for labels */
.v-input__slot {
  overflow: visible !important;
  padding-top: 8px !important;
}

.v-select__selections {
  overflow: hidden !important;
}

.v-chip {
  overflow: hidden !important;
}

/* Card content overflow */
.v-card-text {
  overflow: hidden !important;
}

/* List items overflow */
.space-y-2 > *,
.space-y-3 > * {
  overflow: hidden !important;
}
.form-title {
  font-size: 1.25rem;
  padding: 0px !important;
  font-weight: 600;
  color: #21427d;
  margin: 0;
}

.inline-flex.items-center.bg-blue-100.rounded-full {
  display: inline-flex !important;
  align-items: center !important;
  background-color: #dbeafe !important;
  border-radius: 9999px !important;
  font-size: 0.875rem !important;
  font-weight: 500 !important;
  color: #1e40af !important;
}
.v-application p {
  margin-bottom: 0px !important;
}
</style>
